/**
 * /frontend/src/features/analytics/pages/AnomalyAnalyticsPage.js
 */

import React, { useEffect, useMemo } from 'react';
import useAnomalyAnalytics from '../hooks/useAnomalyAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import AnomalyAlertBanner from '../components/anomalies/AnomalyAlertBanner';
import AnomalyTimeline from '../components/anomalies/AnomalyTimeline';
import { toAnomalyTimelineSeries } from '../utils/chartDataTransformers';
import './AnomalyAnalyticsPage.css';

/**
 * Anomaly detection page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const AnomalyAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const hook = useAnomalyAnalytics({ request, storeToken, storeUuid, startDate, endDate, refreshSignal });
  const active = useMemo(() => toAnomalyTimelineSeries(hook.data?.active), [hook.data?.active]);
  const history = useMemo(() => toAnomalyTimelineSeries(hook.data?.history), [hook.data?.history]);
  const isEmpty = useMemo(() => active.length === 0 && history.length === 0, [active.length, history.length]);
  const syncing = hook.isLoading || hook.isRefreshing;

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No anomalies detected for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <AnomalyAlertBanner items={active} />
      <AnomalyTimeline items={history} />
    </div>
  );
};

export default AnomalyAnalyticsPage;
